// Level-up and mastery pop-ups, one after another. They never lock input: queued farm work
// keeps running behind them; a tap anywhere closes the current one.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { audioEvents } from '../audio/audioEvents.js';
import { createPopupQueue, levelUpLabels, masteryLabels, welcomeBackLine } from './popupQueue.mjs';
import { vehicleKey } from './vehicles.mjs';
import { giftsView, giftLine } from './fuelGifts.mjs';
import { helpersText } from './visitHelp.mjs';
import { social } from '../api/social.js';
import { gamePanel } from '../ui/gameUi.js';
import { iconText } from '../ui/iconText.js';
import { serverNow } from '../state/serverClock.mjs';

const FONT = 'system-ui, -apple-system, sans-serif';
const DEPTH = 2100;
const readyKey = (id) => `farm_crop_${id}_ready`;

// The banner shows once per welcome-back hour, not on every reload in it: remembered by its end.
const SEEN_KEY = 'sd_welcome_back_seen';
const seenStart = () => { try { return localStorage.getItem(SEEN_KEY); } catch { return null; } };
let welcomeShown = null;      // this page load's memory, when storage is unavailable
const markSeen = (iso) => { welcomeShown = iso; try { localStorage.setItem(SEEN_KEY, iso); } catch { /* private window */ } };

// `ownFarm: false` (a friend's farm): only what `show` is given — none of your own farm's banners on arrival.
export function attachFarmPopups(scene, { onGiftsSeen, onHelpersSeen, ownFarm = true } = {}) {
  const queue = createPopupQueue();
  let open = null;

  const card = (h, onClose = null) => {
    const layer = scene.add.container(0, 0).setDepth(DEPTH).setScrollFactor(0);
    const overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.45).setOrigin(0, 0).setInteractive();
    const panel = gamePanel(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2, 560, h);   // the game look (gameUi.js)
    layer.add([overlay, panel]);
    overlay.on('pointerup', () => { layer.destroy(true); open = null; onClose?.(); showNext(); });
    layer.setScale(0.85).setAlpha(0);
    scene.tweens.add({ targets: layer, scale: 1, alpha: 1, duration: 220, ease: 'Back.Out' });
    return layer;
  };
  const text = (x, y, s, size, color = '#000') => iconText(scene, x, y, s, { fontFamily: FONT, fontSize: `${size}px`, color, fontStyle: 'bold', align: 'center' }).setOrigin(0.5);
  const cropIcon = (id, x, y, scale) => (scene.textures.exists(readyKey(id))
    ? scene.add.image(x, y, readyKey(id)).setScale(scale)
    : scene.add.circle(x, y, 26, 0x7ed957));

  const coinsFly = (from, amount) => {
    for (let i = 0; i < Math.min(8, Math.max(3, Math.round(amount / 20))); i++) {
      const c = text(from.x, from.y, '{coin}', 26).setDepth(DEPTH + 1).setScrollFactor(0);
      scene.tweens.add({ targets: c, x: GAME_WIDTH - 90, y: 36, delay: 120 * i, duration: 650, ease: 'Cubic.In', onComplete: () => c.destroy() });
    }
  };

  const levelUp = (e) => {
    const labels = levelUpLabels(e, gameState.raw?.catalog?.farm?.crops ?? {}, window.__myCharacter ?? 'natalka', gameState.raw?.catalog?.farm?.tools ?? {});
    const hasUnlocks = labels.items.length > 0;
    const layer = card(hasUnlocks ? 440 : 330);
    const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2 - (hasUnlocks ? 40 : 0);
    const star = text(cx, cy - 110, '{xp_star}', 90);
    scene.tweens.add({ targets: star, angle: 360, duration: 900, ease: 'Cubic.Out' });
    layer.add([star, text(cx, cy - 30, `Poziom ${e.level}!`, 40), text(cx, cy + 20, `+${e.gift} {coin}`, 26, '#b8860b'),
      text(cx, cy + 50, labels.gift, 16, '#6b5a3a')]);
    if (hasUnlocks) {
      layer.add(text(cx, cy + 95, labels.heading, 20, '#1a6b1a'));
      labels.items.forEach((item, i) => {
        const x = cx + (i - (labels.items.length - 1) / 2) * 130;
        const vk = item.kind === 'tool' ? vehicleKey(item.id, item.size) : null;
        const icon = item.kind === 'crop' ? cropIcon(item.id, x, cy + 160, 0.3)
          : vk && scene.textures.exists(vk) ? scene.add.image(x, cy + 160, vk).setDisplaySize(100, 100 * scene.textures.get(vk).getSourceImage().height / scene.textures.get(vk).getSourceImage().width)
          : text(x, cy + 160, item.kind === 'tool' ? '{tractor}' : '{seedling}', 34);
        layer.add([icon, text(x, cy + 212, item.name, 16, '#333')]);
      });
    }
    coinsFly({ x: cx, y: cy + 20 }, e.gift);
    audioEvents.emit('upgrade');
  };

  const mastery = (e) => {
    const labels = masteryLabels(e, gameState.raw?.catalog?.farm);
    const layer = card(labels.plaque ? 400 : 370);
    const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2 - 15;
    const name = gameState.raw?.catalog?.farm?.crops?.[e.crop]?.name_pl ?? e.crop;
    layer.add([cropIcon(e.crop, cx, cy - 90, 0.5), text(cx, cy - 10, `Mistrz: ${name}`, 30)]);
    for (let i = 0; i < 3; i++) {
      const s = text(cx + (i - 1) * 56, cy + 45, i < e.stars ? '{star_full}' : '{star_empty}', 48, '#e0a800').setScale(i === e.stars - 1 ? 0 : 1);
      layer.add(s);
      if (i === e.stars - 1) scene.tweens.add({ targets: s, scale: 1, duration: 420, delay: 200, ease: 'Back.Out' });
    }
    layer.add(text(cx, cy + 100, labels.price, 20, '#1a6b1a'));             // what the star is for: a better price, for good
    layer.add(text(cx, cy + 135, labels.reward, 20, '#b8860b'));
    if (labels.plaque) layer.add(text(cx, cy + 170, labels.plaque, 17, '#8a5a00'));
    coinsFly({ x: cx + 60, y: cy + 135 }, e.coins);   // from the reward line, not over the price
    audioEvents.emit('upgrade');
  };

  const welcomeBack = (e) => {
    const layer = card(300);
    const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2;
    const sun = text(cx, cy - 80, '{sun}', 80);
    scene.tweens.add({ targets: sun, scale: 1.12, yoyo: true, repeat: 2, duration: 380, ease: 'Sine.InOut' });
    const line = welcomeBackLine({ rested_until_iso: e.until_iso }, serverNow()) ?? 'Podwójne XP przez godzinę {xp_star}';
    layer.add([sun, text(cx, cy + 5, 'Witaj z powrotem!', 36), text(cx, cy + 55, line, 22, '#b8860b')]);
    audioEvents.emit('upgrade');
  };

  // Friends' canisters (spec 2026-09-29-farm-fuel-gifts): who sent them, a one-tap thank-you each.
  const fuelGifts = async (e) => {
    const giftsN = e.gifts.length;
    const rowsN = giftsView(e.gifts).rows.length;      // one row per friend, "×3" when they sent more
    const h = 200 + 52 * rowsN;
    const layer = card(h, () => onGiftsSeen?.(giftsN));   // only these: newer arrivals wait for the next pop-up
    const cx = GAME_WIDTH / 2, top = GAME_HEIGHT / 2 - h / 2;
    layer.add([text(cx, top + 48, '{canister}', 54), text(cx, top + 100, 'Prezenty od przyjaciół', 28)]);
    audioEvents.emit('buy');
    const friends = await social.listFriends().catch(() => []);
    if (!layer.active) return;                          // closed while the list loaded
    const view = giftsView(e.gifts, new Set(friends.filter((f) => f.gift_sent_today).map((f) => f.id)));
    view.rows.forEach((r, i) => {
      const y = top + 150 + i * 52;
      const label = text(cx - 90, y, giftLine(r), 18, '#333');
      if (label.displayWidth > 330) label.setScale(330 / label.displayWidth);   // a long name: shrink to fit beside the button
      const btn = scene.add.rectangle(cx + 160, y, 170, 40, r.thank.enabled ? 0x7ed957 : 0xdddddd, 1).setStrokeStyle(2, 0x333333);
      const btnText = text(cx + 160, y, r.thank.label, 16, r.thank.enabled ? '#000' : '#888');
      layer.add([label, btn, btnText]);
      if (!r.thank.enabled) return;
      btn.setInteractive({ useHandCursor: true }).on('pointerup', async () => {
        btn.disableInteractive().setFillStyle(0xdddddd); btnText.setText('{check} Wysłano').setColor('#888');
        try { await social.sendFuelGift(r.id); audioEvents.emit('buy'); } catch (err) {
          if (err?.error_code !== 'GIFT_ALREADY_SENT') { btn.setInteractive({ useHandCursor: true }).setFillStyle(0x7ed957); btnText.setText(r.thank.label).setColor('#000'); }
        }
      });
    });
    layer.add(text(cx, top + h - 36, view.total, 22, '#b8860b'));
  };

  // Farm 5: who helped on this farm since it was last opened (farm.helpers), then forgotten.
  const helpers = (e) => {
    const lines = helpersText(e.helpers);
    const h = 170 + 40 * lines.length;
    const layer = card(h, () => onHelpersSeen?.(Object.keys(e.helpers)));   // only these: later helpers wait
    const cx = GAME_WIDTH / 2, top = GAME_HEIGHT / 2 - h / 2;
    layer.add([text(cx, top + 46, '{heart}', 50), text(cx, top + 96, 'Znajomi Ci pomogli', 28)]);
    lines.forEach((l, i) => {
      const t = text(cx, top + 142 + i * 40, l, 17, '#333');
      if (t.displayWidth > 520) t.setScale(520 / t.displayWidth);   // a long name: shrink the line to fit the card
      layer.add(t);
    });
    audioEvents.emit('buy');
  };

  const showNext = () => {
    if (open) return;
    const e = queue.next();
    if (!e) return;
    open = e;
    ({ level_up: levelUp, mastery, welcome_back: welcomeBack, fuel_gifts: fuelGifts, helpers })[e.type](e);
  };

  // The hour usually starts on the game's first state load (before any action), so entering the
  // farm inside a fresh hour shows the banner too — once per hour, whichever way it arrives.
  // Keyed by the hour's end, which the event carries (the state may not have caught up yet).
  const seen = (iso) => !!iso && (welcomeShown === iso || seenStart() === iso);
  const show = (events) => {
    const list = (events ?? []).filter((e) => e?.type !== 'welcome_back' || !seen(e.until_iso));
    for (const e of list) if (e?.type === 'welcome_back' && e.until_iso) markSeen(e.until_iso);
    queue.push(list);
    showNext();
  };
  if (!ownFarm) return { show };
  const farm = gameState.raw?.farm;
  if (welcomeBackLine(farm, serverNow())) show([{ type: 'welcome_back', until_iso: farm.rested_until_iso }]);
  const gifts = gameState.raw?.farm?.new_gifts ?? [];
  if (gifts.length) show([{ type: 'fuel_gifts', gifts }]);
  const helped = gameState.raw?.farm?.helpers ?? {};
  if (Object.keys(helped).length) show([{ type: 'helpers', helpers: helped }]);
  return { show };
}
